import GeneralFormFields, { FormFieldActions, RequestStatus } from "components/GeneralFormView/types";
import { FormFieldsBuilder } from "components/GeneralFormView/Utils/FormFieldsBuilder";
import { QuestionPreviewType } from "mobx-store/storeInterfaces/surveyInterface";
import { FacilityFormViewModel } from "./facilityFormViewModel";
import * as yup from 'yup';
import React from 'react';
import GeneralFormView from "components/GeneralFormView";
import { useObserver } from "mobx-react";
import { FacilityDTO } from "./facilityDTO";
import { stores } from "mobx-store";
import Condition, { ConditionalStatementTypes, ConditionExpression, ConditionStatement } from "components/GeneralFormView/Utils/Condition";
import { getIn } from "formik";
import { facilityToDTO } from "./facility.mapper";
import { ICountry } from "mobx-store/storeInterfaces/authInterface";
import SnackBarComponent from "components/SnackBarComponent/SnackBarComponent";

const AddEditFacilityForm: React.FC = () => {
  const optionsForm = new FormFieldsBuilder<FacilityFormViewModel, FacilityFormViewModel['questions'][number]['options']>()
  .addField('text', {
    title: 'option title',
    type: GeneralFormFields.INPUT_FIELD,
    hasTranslations: true,
    initialValue: '',
    fieldOptions: {
    },
    validationSchema: yup.string().optional()
  });

  const questionsForm = new FormFieldsBuilder<FacilityFormViewModel, FacilityFormViewModel['questions'][number]>()
    .addField('title', {
      title: 'question title',
      type: GeneralFormFields.INPUT_FIELD,
      hasTranslations: true,
      initialValue: '',
      fieldOptions: {
      },
      validationSchema: yup.string().required("Please fill this field"),
    })
    .addField('isMandatory', {
      hasTranslations: false,
      title: 'Mandatory',
      initialValue: false,
      validationSchema: yup.boolean().optional(),
      type: GeneralFormFields.TOGGLE_FIELD,
      fieldOptions: {
      },
    })
    .addField('isOther', {
      hasTranslations: false,
      title: 'Other',
      initialValue: false,
      validationSchema: yup.boolean().optional(),
      type: GeneralFormFields.TOGGLE_FIELD,
      fieldOptions: {
      },
    })
    .addField('type', {
      title: 'type',
      type: GeneralFormFields.SELECT_FIELD,
      hasTranslations: true,
      initialValue: ['MCQ'],
      fieldOptions: {
        isMultiple: false,
        items: [
          {
            title: 'MCQ',
            value: 'MCQ',
          }
        ],
      },
    })
    .addField('questionPreview', {
      title: 'questionPreview',
      type: GeneralFormFields.SELECT_FIELD,
      hasTranslations: true,
      initialValue: [QuestionPreviewType.SINGLE_SELECT],
      fieldOptions: {
        isMultiple: false,
        items: [
          {
            title: QuestionPreviewType.SINGLE_SELECT,
            value: QuestionPreviewType.SINGLE_SELECT,
          },
          {
            title: QuestionPreviewType.MULTIPLE_SELECT,
            value: QuestionPreviewType.MULTIPLE_SELECT,
          },
          {
            title: QuestionPreviewType.SELF_SELECT,
            value: QuestionPreviewType.SELF_SELECT,
          }
        ],
      },
    })
    .addField('questionOptions', {
      title: 'Options',
      type: GeneralFormFields.FIELD_ARRAY,
      hasTranslations: true,
      validationSchema: yup.array()
      .when(["questionPreview"],{
        is: (e) => {
          return e[0] === QuestionPreviewType?.SELF_SELECT;
        },
        then: yup.array().of(yup.object().shape({
          text: yup.string().optional()
        })),
        otherwise: yup.array().of(yup.object().shape({
          text: yup.string().required("Please fill this field")
        }))
      }),
      fieldOptions: {
        isCollapsible: false,
        formData: {
          title: '',
          type: GeneralFormFields.NESTED_FORM,
          fieldOptions: {
            formData: optionsForm.formFields,
            isCollapsibleInArray: false,
            isCollapsible: false,
          },
        },
      },
      condition: (values, actions, location) => new Condition({
        values,
        initialStatement: new ConditionStatement({
          action: actions[FormFieldActions.HIDE],
          type: ConditionalStatementTypes.IF,
          expression: new ConditionExpression({
            values,
            expression: (val) => {
              console.log('valvalval', val)
              const value = getIn(val, location.split('.').slice(0, -1).join('.')).type;
              const previewType = getIn(val, location.split('.').slice(0, -1).join('.')).questionPreview;
              if (value[0] === 'TEXT' || value[0] === 'RATING' || (previewType && previewType[0] === QuestionPreviewType.SELF_SELECT)) {
                return true;
              }
              return false;
            },
          }),
        }),
      }).addElse(new ConditionStatement({
        action: actions[FormFieldActions.ENABLE],
        type: ConditionalStatementTypes.ELSE,
        expression: new ConditionExpression({
          values,
          expression: (val) => true,
        }),
      })),
    });

    const {
      formFields,
    } = new FormFieldsBuilder<FacilityFormViewModel>()
      .addField('title', {
        fieldOptions: {
        },
        hasTranslations: true,
        title: 'Title',
        type: GeneralFormFields.INPUT_FIELD,
      })
      .addField('questions', {
        title: 'Questions',
        type: GeneralFormFields.FIELD_ARRAY,
        hasTranslations: true,
        fieldOptions: {
          isCollapsible: false,
          formData: {
            title: '',
            type: GeneralFormFields.NESTED_FORM,
            validationSchema: yup.array(yup.object({
              title: yup.string().required(),
              isMandatory: yup.boolean().default(false),
              type: yup.array(yup.string().required()).required(),
              options: yup.array(yup.object({
                text: yup.string().required(),
              }))
            })),
            fieldOptions: {
              formData: questionsForm.formFields,
              isCollapsible: false,
              isCollapsibleInArray: true,
              titleColumn: 'title',
            },
          },
        },
      });

  const parentRoute = localStorage.getItem('parentRoute');

  const getDeletedQuestionsIds = (updatedQuestions: Array<{questionId: number}>) => {
    const {
      questions: formQuestions,
    } = stores.facilityFormStore.selectedFacilityForm;
    const formQuesitonsIds: number [] = formQuestions.map((fq: {questionId: number}) => fq.questionId);
    const updatedQuestionsIds: number [] = updatedQuestions.map((uq) => uq.questionId);
    return formQuesitonsIds.filter((id) => !updatedQuestionsIds.includes(id));
  };

  return useObserver(() => (
    <GeneralFormView<FacilityDTO, FacilityFormViewModel>
      defaultLang="ar"
      title="Facility form"
      identifier="id"
      otherLanguages={[
        'en'
      ]}
      formData={formFields}
      create={async (formValue) => {
        formValue.availableCountries = await stores.countryStore.managedCountries
          .map((country: ICountry) => country.isoCode);
          const toDto = facilityToDTO(Object.create(formValue))
          const res = await stores.facilityFormStore.create(toDto)
        return res;
      }}
      update={async (id, formValue, language) => {
        try {
          formValue.availableCountries = await stores.countryStore.managedCountries
            .map((country: ICountry) => country.isoCode);
          formValue.deletedQuestionsIds = getDeletedQuestionsIds(formValue.questions);
          const toDto = facilityToDTO(Object.create(formValue));
          await stores.facilityFormStore.update(toDto, +id, language);
        } catch (error) {
          SnackBarComponent.getInstance().openSnackbar((error as any).response.data.message, 3000, 'error');
        }
      }}
      get={async (id, language) => {
        await stores.facilityFormStore.getOne(+id, language);
        await stores.countryStore.getUserManagedCountries();
        return new FacilityFormViewModel(stores.facilityFormStore.selectedFacilityForm as any);
      }}
      isFetchSuccessful={stores.facilityFormStore.loadingState === RequestStatus.SUCCESS}
      createOrUpdateStatus={stores.facilityFormStore.addEditLoadingState}
      postSubmissionRoute={parentRoute || ''}
    />
  ));
};

export default AddEditFacilityForm;


